import { useCallback, useEffect, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Spin, Alert } from "antd"
import { TempLayout } from "../../layouts"
import { useSapProductStore } from "../../stores"
import SapMasterProductDetailView from "./components/SapMasterProductDetailView"
import ApprovalActions from "../../atoms/approval-actions"
import { ISapSanitizedProduct } from "../../types"
import { getProductEditAccess } from "./utils/productEditAccess"

const SapMasterProductsDetail = () => {
  const { recordId } = useParams()
  const navigate = useNavigate()
  const { fetchProductByRecordId, productDetail, isLoading, error } = useSapProductStore()
  const currentSession = useMemo(() => {
    try {
      return JSON.parse(window.localStorage.getItem("session_farms") || "null") as { employee_id?: string } | null
    } catch (_error) {
      return null
    }
  }, [])

  const sanitizedApprovalData = {
    status: 200,
    data: productDetail?.approval_process_id
  }

  const sanitizedProductDetail: ISapSanitizedProduct = {
    ...productDetail,
    approval_process_id: sanitizedApprovalData
  }
  const editAccess = getProductEditAccess(productDetail?.approval_process_id, currentSession?.employee_id || null)
  const canEditProduct = editAccess.canEdit && (productDetail?.approval_status || "").toUpperCase() !== "APPROVED"

  const handleApproveClick = () => {
    navigate("/sapprd/history")
  }

  const fetchProductById = useCallback(async () => {
    if (recordId) {
      await fetchProductByRecordId(recordId)
    }
  }, [recordId, fetchProductByRecordId])

  useEffect(() => {
    void fetchProductById()
  }, [fetchProductById])

  useEffect(() => {
    const requesterName = productDetail?.fullname?.trim()
    const currentRecordId = productDetail?.record_id || recordId || ""
    document.title = requesterName ? `${requesterName} - SAP Product${currentRecordId ? ` (${currentRecordId})` : ""}` : "SAP - Product Detail"

    return () => {
      document.title = "FARMS Application"
    }
  }, [productDetail?.fullname, productDetail?.record_id, recordId])

  return (
    <TempLayout title="SAP - Product Detail" navNavigateUrl="/sapprd/history">
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert message="Error" description={error} type="error" showIcon />
      ) : productDetail ? (
        <SapMasterProductDetailView data={sanitizedProductDetail} />
      ) : (
        <Alert message="Not Found" description="Product not found or ID missing" type="warning" showIcon />
      )}

      {productDetail && (
        <ApprovalActions
          editNavigateUrl={`/sapprd/edit/${recordId}`}
          canEditData={canEditProduct}
          mainData={sanitizedProductDetail}
          onApproveClick={handleApproveClick}
        />
      )}
    </TempLayout>
  )
}

export default SapMasterProductsDetail
